import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useUpdateEffect } from 'react-use';
import _uniqBy from 'lodash/uniqBy';
import { addMultipleToCart } from 'ck-utils';
import { FreeGift, Nudge } from './FreeGift';
import styles from './CartFreeGifts.module.scss';

/**
 * Get eligible free gifts and it is eligible quantities
 * @param {object} cart - Shopify cart object
 * @returns {object} - Eligible free gifts { product-handle: quantity }
 */
const getEligibleFreeGifts = (cart) => {
  if (!cart?.items?.length > 0) return {};
  try {
    const { freeGifts, cartItems } = cart.items.reduce((result, item) => {
      result.cartItems[item.handle] = result.cartItems[item.handle] ? result.cartItems[item.handle] + item.quantity : item.quantity;
      if (item.properties && item.properties._applicable_free_gifts) {
        const itemFreeGiftsHandles = JSON.parse(item.properties._applicable_free_gifts);
        const itemFreeGifts = itemFreeGiftsHandles.reduce((acc, handle) => {
          acc[handle] = acc[handle] ? acc[handle] + 1 : 1;
          return acc;
        }, {});

        Object.keys(itemFreeGifts).forEach((key) => {
          const itemGiftQty = itemFreeGifts[key];
          const giftQty = result.freeGifts[key];
          result.freeGifts[key] = giftQty && giftQty > itemGiftQty ? giftQty : itemGiftQty;
        });
      }
      return result;
    }, { freeGifts: {}, cartItems: {} });

    return Object.keys(freeGifts).reduce((acc, key) => {
      if (!cartItems[key] || cartItems[key] < freeGifts[key]) {
        acc[key] = cartItems[key] ? freeGifts[key] - cartItems[key] : freeGifts[key];
      }
      return acc;
    }, {});
  } catch (e) {
    return {};
  }
};

const CartFreeGifts = ({ nfsTiersBlocks, categoryOrGiftCardGiftsBlocks, isCartPage }) => {
  const isCartNudgeActive = nfsTiersBlocks.length > 0 || categoryOrGiftCardGiftsBlocks.length > 0;
  const [cart, setCart] = useState(null);

  useEffect(() => {
    if (!isCartNudgeActive) return undefined;
    const handleSetCart = e => setCart(e.detail.cart);
    window.addEventListener('afterGetCart', handleSetCart, { passive: true });
    if (isCartPage) { // only do the initial fetch if in cart page
      window.ShopifyAPI.getCart();
    }
    return () => {
      window.removeEventListener('afterGetCart', handleSetCart);
    };
  }, [isCartNudgeActive, isCartPage]);

  const eligibleFreeGifts = getEligibleFreeGifts(cart);

  // Combine all blocks and separate them into groups: the AutoAddToCart group VS. the ManualAddToCartGroup
  // If a product has more than one variants, it goes to the manual group
  const { nudgeOnlyBlocks, autoAddToCartGifts, manualAddToCartGifts } = _uniqBy(
    [...nfsTiersBlocks, ...categoryOrGiftCardGiftsBlocks],
    'handle'
  ).reduce(
    (acc, block) => {
      // Nudge only blocks, e.g. Free shipping
      if (block.tier_value && !block.handle && block.promotionbar_before_text) {
        acc.nudgeOnlyBlocks.push(block);
        return acc;
      }

      // Invalid Gift or ineligible gift
      if (
        !block.handle
        || !eligibleFreeGifts[block.handle]
      ) return acc;

      // Valid gift
      if (block.variants.length === 1) {
        const variant = block.variants[0];
        acc.autoAddToCartGifts.push({
          id: variant.id,
          quantity: eligibleFreeGifts[block.handle],
        });
      } else {
        acc.manualAddToCartGifts.push(block);
      }

      return acc;
    },
    { nudgeOnlyBlocks: [], autoAddToCartGifts: [], manualAddToCartGifts: [] }
  );

  // Normalise the auto add to cart items and add them to cart
  useUpdateEffect(() => {
    addMultipleToCart(autoAddToCartGifts);
  }, [autoAddToCartGifts]);

  // Early exit
  if (!isCartNudgeActive || !cart?.items?.length > 0) return null;

  // Get Cart total, excluding gift card line items
  const total = cart.items.reduce((acc, item) => (item.gift_card ? acc - item.final_line_price : acc), cart.total_price) / 100;

  // reach which tier
  const belowTierFreeGifts = nfsTiersBlocks.reduce((acc, block) => {
    if (
      !block.handle
      || total >= Number(block.tier_value)
      || total <= Number(block.tier_min)
    ) return acc;
    acc.push({ ...block, isBelowTierThreshold: true });
    return acc;
  }, []);

  const nudgeOnlyItems = nudgeOnlyBlocks.filter(item => total > Number(item.tier_min));

  if (nudgeOnlyItems.length === 0 && manualAddToCartGifts.length === 0 && belowTierFreeGifts.length === 0) return null;

  return (
    <div className={classnames(styles.root, { [styles.miniCartMargin]: !isCartPage })}>
      {nudgeOnlyItems.map(item => <Nudge key={item.id} item={item} total={total} />)}
      {manualAddToCartGifts.map(item => <FreeGift key={item.id} item={item} />)}
      {belowTierFreeGifts.map(item => <FreeGift key={item.id} item={item} total={total} showAddToCart={false} />)}
    </div>
  );
};

CartFreeGifts.propTypes = {
  nfsTiersBlocks: PropTypes.array,
  categoryOrGiftCardGiftsBlocks: PropTypes.array,
  isCartPage: PropTypes.bool
};

CartFreeGifts.defaultProps = {
  nfsTiersBlocks: [],
  categoryOrGiftCardGiftsBlocks: [],
  isCartPage: false
};

export { CartFreeGifts };
